import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { useMutation } from "graphql-hooks";
import { useNavigate, useParams } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { InputAdornment, IconButton } from "@mui/material";
import {
  Card,
  CardContent,
  Container,
  Divider,
  Typography,
  Button,
  FormHelperText,
  TextField,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import logoImg from "./assets/logo.png";
import loginImg from "./assets/login-image.png";
import mascotJoin from "./assets/login-mascot.png";

import useIsTablet from "./hooks/useIsTablet";
import useIsMobile from "./hooks/useIsMobile";

import { RESET_PASSWORD_MUTATION } from "./graphQL/user_qry";

const StyledContainer = styled(Container)(() => ({
            
    minHeight: 624,
    angle: "0 deg",
    opacity: 1,
    borderRadius: "16px",
    boxShadow: "0px 4px 12px 0px #0000001F",
    display: "flex",
    justifyContent: "center",
    justifyItems: "center" ,

    // DESKTOP
    maxWidth: "1080px",
    margin: "48px 150px",

    "@media (max-width: 1360px)": {
        maxWidth: "802px",      // TABLET WIDTH
        margin: "32px auto",
    },

    "@media (max-width: 856px)": {
        maxWidth: "361px",      // MOBILE WIDTH
        margin: "8px auto",
    },
    
}));

const FormSide = styled(Box)(() => ({
  
   width: "540px",             // Desktop 2 columns = 1080 total
   padding: "32px",
   gap: "16px",

  "@media (max-width: 1360px)": 
  { width: "401px" },// Tablet 2 columns = 802 total
  
  "@media (max-width: 856px)":  
  {  width: "100%",            // Mobile = single column
     padding: "16px",          // Reduce padding on mobile },
     height: "auto",
     minHeight: "auto"
  },
  minHeight: 624,
  
  angle: "0 deg",
  opacity: 1,
}));

const StyledTextField = styled(TextField)(() => ({
    marginBottom: 5,
    marginTop: 30,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontSize: 16,
    },
}));

const StyledFormHelperTextError = styled(FormHelperText)(() => ({
    marginTop: 0,
    fontSize: 14, //16,
    fontFamily: "Arial",
}));

const StyledBox  = styled(Box)(() => ({
    color: "#212121",
    backgroundColor: "rgba(239, 86, 88, 0.1)",
    borderRadius: 12,
    padding: 16,
    fontSize: 16,
    fontFamily: "Arial",
    lineHeight: 1.4,
}));

const StyledButton = styled(Button)(() => ({
    width: "158px",
    height: "48px",
    backgroundColor: "#EF5658",
    textTransform: "none",
    "&:active": {
      backgroundColor: "#EF5658",
    },
    "&:hover": {
      backgroundColor: "#D33338",
    },
}));

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const UpdatePassword = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const navigate = useNavigate();
  const form = useRef(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [password, setPassword] = useState("");
  const [retypePassword, setRetypePassword] = useState("");
  const { forgetCode } = useParams();

  const isTablet = useIsTablet(1360); // Use 1360px breakpoint for tablet size
  const isMobile = useIsMobile(856); // Use 856px breakpoint for mobile size

  const [resetPassword] = useMutation(RESET_PASSWORD_MUTATION);

  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const handleToggleRepeatPassword = () => {
    setShowRepeatPassword((prev) => !prev);
  };

 
  setTimeout(() => {
    setSuccessMsg("");
    setErrorMsg("");
  }, 30000);

  const passwordChange = event => {
    setPassword(event.target.value);
  };

  const retypePasswordChange = event => {
    setRetypePassword(event.target.value);
  };

  const onSubmit = async formData => {
    await sleep(2000);

    if (formData.password === formData.retypePassword) {
      const { data, error } = await resetPassword({
        variables: {
          password: formData.password,
          forgetCode: forgetCode,
        },
      });
      if (error) {
        console.log("Error", error, data);
        setErrorMsg(error.graphQLErrors[0].message);
        setTimeout("");
      } else {
        console.log("Success");
        setSuccessMsg("Your new password is set.");
        setTimeout("");
        navigate("/update-password-confirmation");
      }
    } else {
      setErrorMsg("Password and re-enter password must be the same.");
      setTimeout("");
    }
  };

useEffect(() => {
    window.scrollTo(0, 0);    
  }, []);

  return (
    <Grid>
      <>
        <Helmet>
          <title>R3PLAY - Update Password</title>
          <meta
            name="description"
            content="Create a new password using your reset code"
          />
          <meta
            property="keywords"
            content="Update password, password recovery"
          />        
        </Helmet>
        <form ref={form} onSubmit={handleSubmit(onSubmit)}>            
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            {!isMobile ?
            (
              <StyledContainer>
              <Box                  
                  display="grid"
                  gridTemplateColumns="1fr 1fr"                 
                  justifyContent="center"
                  justifyItems="center"       // center the fixed-width children
                  alignItems="start"
                  gap="0"
                  sx={{ width: "100%" }}
                >
                <FormSide sx={{ borderRight: "1px solid #e0e0e0" }}>
                    <Box
                      flexGrow={1}
                      alignItems="center"
                      display="flex"
                      justifyContent="space-between"
                    >
                     <Typography gutterBottom 
                        variant={!isTablet ? "h2" : "h3"}
                        fontSize={!isTablet ? "36px" : "30px"}
                        sx={{                        
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: "600",
                        lineHeight: "1.75",
                        letterSpacing: "0.00938em"
                      }}>

                        Update Password
                  </Typography>
                </Box>

                  <Box flexGrow={1} mb={1}>
                    <Typography variant="body1">Please enter new password.</Typography>

                      <StyledTextField
                        fullWidth
                        required
                        label="Enter New Password"
                        margin="normal"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        id="password"
                        {...register("password", { required: true })}
                        onChange={passwordChange}

                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleTogglePassword}
                                edge="end"
                                aria-label="toggle password visibility"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />

                    {errors.password && errors.password.type === "required" && (
                        <Box ml={2}>
                          <StyledFormHelperTextError>
                            Password cannot be blank.
                          </StyledFormHelperTextError>
                        </Box>
                    )}
                  <StyledTextField
                        fullWidth
                        required
                        label="Re-enter New Password"
                        margin="normal"
                        name="retypePassword"
                        type={showRepeatPassword ? "text" : "password"}
                        variant="outlined"
                        id="retypePassword"
                        {...register("retypePassword", { required: true })}
                        onChange={retypePasswordChange}

                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleToggleRepeatPassword}
                                edge="end"
                                aria-label="toggle password visibility"
                              >
                                {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                  {errors.retypePassword && errors.retypePassword.type === "required" && (
                    <Box ml={2}>
                      <StyledFormHelperTextError>
                        Re-enter Password cannot be blank.
                      </StyledFormHelperTextError>
                    </Box>
                  )}
                      {errorMsg && (
                        <Box mt={1} mb={1} ml={2}>
                          <StyledFormHelperTextError>
                            {errorMsg}
                          </StyledFormHelperTextError>
                        </Box>
                      )}
                      {successMsg && (
                        <StyledBox mt={2}>
                          {successMsg}
                        </StyledBox>
                      )}
                      <Box mt={4} display="flex">
                        <StyledButton
                          color="primary"
                          type="submit"
                          variant="contained"
                        >
                          SUBMIT
                        </StyledButton>
                      </Box>
                    </Box>
                  </FormSide>

                  <FormSide sx={{paddingLeft: "0 !important"}}>
                    <Box flexGrow={1}>
                      <Box sx={{paddingLeft: "32px"}}>
                        <img src={logoImg} alt="logo" width={"40%"} />
                      </Box>
                      <Typography variant="body1"
                        sx={{
                        color: "rgba(33, 33, 33, 0.8)",
                        fontSize: "1rem",
                        fontStyle: "normal",
                        fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                        fontWeight: "400",
                        lineHeight: "1.7",
                        letterSpacing: "0.00938em",
                        paddingLeft: "32px"
                        }}>
                        <br />
                        Hi dear. Let us facilitate reseting your password
                        <br /><br />
                        R3PLAY platform is a place where toys, stories, and nostalgic joy come to life. It is a social commerce space where you can buy, sell, trade, donate, and share your love of toys with other collectors, kidults, parents, and enthusiasts just like you.
                      </Typography>
                      <Box>
                        <img src={loginImg} alt="logo" style={{
                                  width: isTablet ? "420px" : "540px",
                                 }} /> 
                      </Box>
                    </Box>                    
                  </FormSide>
                </Box>
              </StyledContainer>
               )
            :
            (
              <StyledContainer>
              <Box                  
                  display="grid"
                  gridTemplateColumns="1fr"                  
                  justifyContent="center"
                  justifyItems="center"       // center the fixed-width children                  
                  gap="0"                
                  sx={{ width: "100%" }}
                >

                <FormSide >

                  <Box
                      flexGrow={1}
                      alignItems="center"
                      display="flex"
                      justifyContent="space-between"
                    >
                      <Typography gutterBottom 
                        variant="h3"
                        fontSize="30px"
                        sx={{                        
                        fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                        fontWeight: "600",
                        lineHeight: "1.75",
                        letterSpacing: "0.00938em"
                      }}>
                        Update Password
                      </Typography>
                    </Box>


                  <Box flexGrow={1}>                     
                      <Typography variant="body1"
                        sx={{
                        color: "rgba(33, 33, 33, 0.8)",
                        fontSize: "1rem",
                        fontStyle: "normal",
                        fontFamily: "Roboto,Helvetica,Arial,sans-serif",
                        fontWeight: "400",
                        lineHeight: "1.7",
                        letterSpacing: "0.00938em",
                        
                        }}>
                        <br />
                        Hi dear. Let us facilitate reseting your password
                        <br /><br />
                        R3PLAY platform is a place where toys, stories, and nostalgic joy come to life. It is a social commerce space where you can buy, sell, trade, donate, and share your love of toys with other collectors, kidults, parents, and enthusiasts just like you.
                      </Typography>
                      <Box>
                            <img src={loginImg} alt="logo" width={"100%"} />
                      </Box>
                      
                    </Box>                    
                  </FormSide>

                  <FormSide sx={{ borderRight: "none" }}>
                    
                  <Box flexGrow={1} mb={1}>
                    <Typography variant="body1">Please enter new password.</Typography>
                        <StyledTextField
                        fullWidth
                        required
                        label="Enter New Password"
                        margin="normal"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        id="password"
                        {...register("password", { required: true })}
                        onChange={passwordChange}

                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleTogglePassword}
                                edge="end"
                                aria-label="toggle password visibility"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />

                    {errors.password && errors.password.type === "required" && (
                        <Box ml={2}>
                          <StyledFormHelperTextError>
                            Password cannot be blank.
                          </StyledFormHelperTextError>
                        </Box>
                    )}
                  <StyledTextField
                        fullWidth
                        required
                        label="Re-enter New Password"
                        margin="normal"
                        name="retypePassword"
                        type={showRepeatPassword ? "text" : "password"}
                        variant="outlined"
                        id="retypePassword"
                        {...register("retypePassword", { required: true })}
                        onChange={retypePasswordChange}

                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={handleToggleRepeatPassword}
                                edge="end"
                                aria-label="toggle password visibility"
                              >
                                {showRepeatPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          )
                        }}
                      />
                  {errors.retypePassword && errors.retypePassword.type === "required" && (
                    <Box ml={2}>
                      <StyledFormHelperTextError>
                        Re-enter Password cannot be blank.
                      </StyledFormHelperTextError>
                    </Box>
                  )}
                      {errorMsg && (
                        <Box mt={1} mb={1} ml={2}>
                          <StyledFormHelperTextError>
                            {errorMsg}
                          </StyledFormHelperTextError>
                        </Box>
                      )}
                      {successMsg && (
                        <StyledBox mt={2}>
                          {successMsg}
                        </StyledBox>
                      )}
                      <Box mt={4} display="flex">
                        <StyledButton
                          color="primary"
                          type="submit"
                          variant="contained"
                        >
                          SUBMIT
                        </StyledButton>
                      </Box>
                    </Box>
                  </FormSide>

                  </Box>
              </StyledContainer>
            )
          }

                {!isTablet && !isMobile
                  ?
                  (
                    <Box
                      sx={{
                        position: "relative",
                        bottom: "-130px",
                        right: 145,
                        cursor: "pointer",
                      }}
                    >
                      <Box
                        component="img"
                        src={mascotJoin}
                        alt="Join the Fun Mascot Orange"
                        sx={{
                          width: "140px",
                          height: "auto",
                          
                        }}
                      />
                    </Box>
                  )
                  :""
                }
          </Box>
          </form>
        </>
    </Grid>
  );
};

export default UpdatePassword;
